import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFinanceSummary } from "../api/finance.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Summary {
  revenueThisMonth: number;
  expensesThisMonth: number;
  grossProfitThisMonth: number;
  overdueInvoices: number;
  activeInvoices: number;
}

function currency(n: number) {
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

export function FinanceOverviewPage() {
  const { token } = useAuth();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void getFinanceSummary(token).then((d) => {
      setSummary(d as Summary);
      setLoading(false);
    });
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Finance & Business Operations</h1>
          <p className="mt-1 text-gray-400">
            Phase 8 — Quotations, Contracts, Invoices, Payments & Expenses
          </p>
        </div>
        {!loading && summary && (
          <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-3">
            {[
              {
                label: "Revenue (30d)",
                value: currency(summary.revenueThisMonth),
                color: "text-green-400",
              },
              {
                label: "Expenses (30d)",
                value: currency(summary.expensesThisMonth),
                color: "text-red-400",
              },
              {
                label: "Gross Profit (30d)",
                value: currency(summary.grossProfitThisMonth),
                color: summary.grossProfitThisMonth >= 0 ? "text-green-400" : "text-red-400",
              },
              { label: "Active Invoices", value: summary.activeInvoices, color: "text-blue-400" },
              {
                label: "Overdue Invoices",
                value: summary.overdueInvoices,
                color: summary.overdueInvoices > 0 ? "text-red-400" : "text-gray-400",
              },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className={`mt-1 text-2xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {[
            { to: "/finance/quotes", label: "Quotations", desc: "Create and manage client quotes" },
            { to: "/finance/contracts", label: "Contracts", desc: "Client agreements & approvals" },
            {
              to: "/finance/invoices",
              label: "Invoices",
              desc: "GST-ready billing & payment tracking",
            },
            {
              to: "/finance/payments",
              label: "Payments",
              desc: "Record and track payments received",
            },
            {
              to: "/finance/expenses",
              label: "Expenses",
              desc: "Track costs by category & vendor",
            },
            {
              to: "/finance/reports",
              label: "P&L Reports",
              desc: "Revenue, expenses, and profitability",
            },
          ].map((nav) => (
            <Link
              key={nav.to}
              to={nav.to}
              className="block rounded-lg border border-gray-800 bg-gray-900 p-4 transition-colors hover:border-indigo-500"
            >
              <p className="font-medium text-white">{nav.label}</p>
              <p className="mt-1 text-sm text-gray-400">{nav.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
